import React, { useMemo, useState } from 'react';
import { Badge } from '@frontend/shadcn/components/ui/badge';
import { Button } from '@frontend/shadcn/components/ui/button';
import { Card, CardContent } from '@frontend/shadcn/components/ui/card';
import { DataTable, TablePagination } from '@frontend/app/refresh-components/DataTableV2';
import { ArrowDownToLineIcon, TableColumnsIcon } from '@revfluence/fresh-icons/regular/esm';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@frontend/shadcn/components/ui/tooltip';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@frontend/shadcn/components/ui/popover';
import { Checkbox } from '@frontend/shadcn/components/ui/checkbox';
import { isEmpty } from 'lodash';
import { format } from 'date-fns';

// Types for automation data
interface AutomationData {
  id: string;
  name: string;
  icon: string;
  totalRuns: number;
  successRate: number;
  status: 'Active' | 'Inactive' | 'Paused' | 'Draft';
  lastUpdated: Date;
  updatedBy: {
    name: string;
    avatar: string;
  };
}

// Dummy data matching the image
const DUMMY_AUTOMATIONS: AutomationData[] = [
  {
    id: '1',
    name: 'Enable Welcome Email',
    icon: '👤',
    totalRuns: 12456,
    successRate: 95,
    status: 'Active',
    lastUpdated: new Date('2025-06-05'),
    updatedBy: { name: 'John Doe', avatar: '/avatar1.jpg' }
  },
  {
    id: '2',
    name: 'Tier unlocked',
    icon: '🔓',
    totalRuns: 0,
    successRate: 0,
    status: 'Inactive',
    lastUpdated: new Date('2025-07-02'),
    updatedBy: { name: 'Jane Smith', avatar: '/avatar2.jpg' }
  },
  {
    id: '3',
    name: 'X conversions away from next tier',
    icon: '📊',
    totalRuns: 34,
    successRate: 97,
    status: 'Paused',
    lastUpdated: new Date('2025-07-02'),
    updatedBy: { name: 'John Doe', avatar: '/avatar1.jpg' }
  },
  {
    id: '4',
    name: 'X sales amount away from next tier',
    icon: '🔒',
    totalRuns: 0,
    successRate: 0,
    status: 'Inactive',
    lastUpdated: new Date('2025-06-01'),
    updatedBy: { name: 'Lisa Wang', avatar: '/avatar3.jpg' }
  },
  {
    id: '5',
    name: 'Reward unlocked',
    icon: '🎁',
    totalRuns: 0,
    successRate: 0,
    status: 'Inactive',
    lastUpdated: new Date('2025-06-01'),
    updatedBy: { name: 'Lisa Wang', avatar: '/avatar3.jpg' }
  },
  {
    id: '6',
    name: 'X sales amount away from reward',
    icon: '💰',
    totalRuns: 0,
    successRate: 0,
    status: 'Draft',
    lastUpdated: new Date('2025-06-01'),
    updatedBy: { name: 'Lisa Wang', avatar: '/avatar3.jpg' }
  },
  {
    id: '7',
    name: 'X conversions away from reward',
    icon: '📈',
    totalRuns: 0,
    successRate: 0,
    status: 'Inactive',
    lastUpdated: new Date('2025-06-01'),
    updatedBy: { name: 'Lisa Wang', avatar: '/avatar3.jpg' }
  },
];

const StatusBadge: React.FC<{ status: AutomationData['status'] }> = ({ status }) => {
  const getStatusVariant = (status: string) => {
    switch (status) {
      case 'Active':
        return 'bg-green-100 text-green-800 hover:bg-green-100';
      case 'Paused':
        return 'bg-yellow-100 text-orange-600 hover:bg-yellow-100';
      case 'Draft':
        return 'bg-blue-100 text-blue-800 hover:bg-blue-100';
      case 'Inactive':
      default:
        return 'bg-gray-100 text-gray-600 hover:bg-gray-100';
    }
  };

  return (
    <Badge variant="secondary" className={getStatusVariant(status)}>
      {status}
    </Badge>
  );
};

const SortButton = ({ column, children }: { column: any; children: React.ReactNode }) => {
  return (
    <Button
      variant="ghost"
      className="px-2 hover:bg-transparent w-full justify-start"
      onClick={() => column.toggleSorting()}
    >
      {children}
    </Button>
  );
};

export const EmailAutomations: React.FC = () => {
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({});
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [data] = useState<AutomationData[]>(DUMMY_AUTOMATIONS);

  const columns = useMemo(() => [
    {
      id: 'automation',
      headerName: 'Automation',
      accessorKey: 'name',
      cell: ({ row }) => (
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-sm">
            {row.original.icon}
          </div>
          <span className="font-medium text-gray-900">{row.original.name}</span>
          {row.original.name === 'X sales amount away from next tier' && (
            <div className="w-4 h-4 rounded-full bg-red-500 flex items-center justify-center">
              <span className="text-white text-xs font-bold">!</span>
            </div>
          )}
        </div>
      ),
      header: ({ column }) => (
        <SortButton column={column}>Automation</SortButton>
      ),
      size: 300,
      minSize: 300,
    },
    {
      id: 'totalRuns',
      headerName: 'Total Runs',
      accessorKey: 'totalRuns',
      cell: ({ row }) => (
        <div className="text-right">
          {row.original.totalRuns > 0 ? row.original.totalRuns.toLocaleString() : '-'}
        </div>
      ),
      header: ({ column }) => (
        <SortButton column={column}>Total Runs</SortButton>
      ),
      size: 150,
      minSize: 150,
    },
    {
      id: 'successRate',
      headerName: 'Success Rate',
      accessorKey: 'successRate',
      cell: ({ row }) => (
        <div className="text-right flex items-center justify-end gap-2">
          {row.original.successRate > 0 ? (
            <>
              <span>{row.original.successRate}%</span>
              <div className="w-1 h-1 rounded-full bg-purple-500" />
            </>
          ) : (
            '-'
          )}
        </div>
      ),
      header: ({ column }) => (
        <SortButton column={column}>Success Rate</SortButton>
      ),
      size: 150,
      minSize: 150,
    },
    {
      id: 'status',
      headerName: 'Status',
      accessorKey: 'status',
      cell: ({ row }) => <StatusBadge status={row.original.status} />,
      header: ({ column }) => (
        <SortButton column={column}>Status</SortButton>
      ),
      size: 120,
      minSize: 120,
    },
    {
      id: 'lastUpdated',
      headerName: 'Last Updated',
      accessorKey: 'lastUpdated',
      cell: ({ row }) => {
        const automation = row.original;
        return (
          <div className="flex items-center gap-2">
            <span>{format(automation.lastUpdated, 'MMM d, yyyy')}</span>
            <div className="w-6 h-6 rounded-full bg-gray-300 flex items-center justify-center">
              <span className="text-xs font-semibold text-white">
                {automation.updatedBy.name.split(' ').map((n) => n[0]).join('')}
              </span>
            </div>
          </div>
        );
      },
      header: ({ column }) => (
        <SortButton column={column}>Last Updated</SortButton>
      ),
      size: 180,
      minSize: 180,
    },
  ], []);

  const totalPages = Math.ceil(data.length / pageSize);
  const paginatedData = useMemo(() => {
    const start = (page - 1) * pageSize;
    const end = start + pageSize;
    return data.slice(start, end);
  }, [data, page, pageSize]);

  const visibleColumns = useMemo(
    () => columns.filter((col) => columnVisibility[col.id] !== false),
    [columns, columnVisibility]
  );

  const exportToCSV = () => {
    const headers = columns.map((col) => col.headerName).join(',');
    const rows = data.map((row) => [
      row.name,
      row.totalRuns,
      `${row.successRate}%`,
      row.status,
      format(row.lastUpdated, 'MMM d, yyyy')
    ].join(','));

    const csvContent = [headers, ...rows].join('\r\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', 'email-automations.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-base font-medium text-grey-6 mb-2">Email Automation</h1>
        <p className="text-grey-5 text-sm">Automate email tasks to easily scale your offers.</p>
      </div>

      <Card className="rounded-2xl">
        <CardContent className="p-0">
          <div className="flex items-center justify-between px-6 py-4 border-b">
            <div className="text-sm font-medium text-foreground">
              {data.length} Automations
            </div>
            <div className="flex items-center gap-2">
              {/* Export Button */}
              <Tooltip>
                <TooltipTrigger>
                  <Button
                    onClick={exportToCSV}
                    variant="ghost"
                    size="icon"
                    disabled={isEmpty(data)}
                    aria-label="Export automations"
                  >
                    <ArrowDownToLineIcon className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Export automations</TooltipContent>
              </Tooltip>

              {/* Column Visibility */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    disabled={isEmpty(data)}
                    aria-label="Toggle column visibility"
                  >
                    <TableColumnsIcon className="h-4 w-4" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="end" className="w-56 p-4">
                  <div className="flex flex-col gap-2">
                    <h4 className="font-medium mb-2">Visible Columns</h4>
                    {columns.map((column) => (
                      <label key={column.id} className="flex items-center gap-2">
                        <Checkbox
                          checked={columnVisibility[column.id] !== false}
                          onCheckedChange={(checked) => {
                            setColumnVisibility((prev) => ({
                              ...prev,
                              [column.id]: checked === true,
                            }));
                          }}
                        />
                        <span>{column.headerName}</span>
                      </label>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>

              {/* Pagination Info */}
              <div className="text-sm text-gray-500 ml-4">
                1 - {Math.min(data.length, pageSize)} of {data.length}
              </div>

              {/* Pagination Controls */}
              <TablePagination
                pageIndex={page - 1}
                pageSize={pageSize}
                totalCount={data.length}
                canPreviousPage={page > 1}
                canNextPage={page < totalPages}
                onPaginationChange={(newPage) => setPage(newPage + 1)}
              />
            </div>
          </div>

          <div className="overflow-hidden">
            <DataTable
              columns={visibleColumns}
              data={paginatedData}
              enableColumnResizing
              getRowId={(row) => row.id}
              columnVisibility={columnVisibility}
              onColumnVisibilityChange={setColumnVisibility}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
